import { Component, type ErrorInfo, type ReactNode } from 'react';
import i18n from 'i18next';

/** Localised string, or the English literal if i18n never initialised (the
 *  boundary can catch errors thrown before/while the bundles load). */
function tr(key: string, fallback: string): string {
  try {
    return i18n.isInitialized && i18n.exists(key) ? i18n.t(key) : fallback;
  } catch {
    return fallback;
  }
}

// Top-level error boundary. Without one, any render error (or a failed lazy
// chunk load) silently unmounts the whole tree to a blank screen — the
// failure becomes invisible. This catches it and shows the message + a
// reload, and logs the full error/stack to the console for debugging.
interface State {
  error: Error | null;
}

const RELOAD_GUARD = 'eb.reloaded';

/** A failed dynamic import (stale chunk after a deploy / offline) — distinct
 *  from a genuine code bug, and recoverable with a reload. Covers both the
 *  404 case (file gone) and the SPA-fallback case (`serve -s`, `vite preview`
 *  hand back index.html, so the import gets HTML and the browser rejects it
 *  with a "module script … MIME type text/html" error). */
function isChunkLoadError(error: Error): boolean {
  const msg = `${error?.name ?? ''} ${error?.message ?? ''}`.toLowerCase();
  return (
    msg.includes('dynamically imported module') ||
    msg.includes('module script') ||
    msg.includes('chunkloaderror') ||
    msg.includes('failed to fetch') ||
    msg.includes('mime type')
  );
}

export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Surfaced in the console with the component stack for diagnosis.
    console.error('Unhandled UI error:', error, info.componentStack);

    // Self-heal the common PWA failure mode: after a new deploy the previous
    // service worker can hand back an index.html that points at chunk hashes
    // which no longer exist, so a lazy import() 404s and the screen blanks.
    // Reload ONCE (guarded so we never loop) to pick up the fresh SW/chunks.
    if (isChunkLoadError(error) && !sessionStorage.getItem(RELOAD_GUARD)) {
      sessionStorage.setItem(RELOAD_GUARD, '1');
      window.location.reload();
    }
  }

  render() {
    const { error } = this.state;
    if (!error) {
      // Recovered (or never failed): clear the one-shot reload guard so a
      // future stale-chunk error in this session can self-heal again. Only
      // ever cleared on a SUCCESSFUL render, so it can't cause a reload loop.
      if (sessionStorage.getItem(RELOAD_GUARD)) sessionStorage.removeItem(RELOAD_GUARD);
      return this.props.children;
    }
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-md bg-bg px-xl text-center">
        <h1 className="text-[20px] font-bold text-on-surface">{tr('errorGeneric', 'Something went wrong')}</h1>
        <pre className="max-w-prose overflow-auto whitespace-pre-wrap rounded-md border border-outline-variant/60 bg-surface p-md text-left text-[12px] text-on-surface-variant">
          {error.message || String(error)}
        </pre>
        <div className="flex gap-sm">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-md bg-primary px-xl py-md text-[15px] font-semibold text-on-primary"
          >
            {tr('reload', 'Reload')}
          </button>
          <button
            type="button"
            onClick={() => { window.location.href = '/'; }}
            className="rounded-md border border-outline px-xl py-md text-[15px] font-semibold text-on-surface"
          >
            {tr('back', 'Home')}
          </button>
        </div>
      </div>
    );
  }
}
